import React from 'react';
import { i18n } from 'config/locales';
import { Summary, NameSummary } from 'components/Summary';
import { FOREIGN, FOREIGN_BUSINESS_FAMILY } from 'config/formSections/foreign';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { foreignBusinessAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import { isPVQForm } from 'helpers/form';
import FamilyItem from './FamilyItem';

const sectionConfig = {
  key: FOREIGN_BUSINESS_FAMILY.key,
  section: FOREIGN.name,
  store: FOREIGN.store,
  subsection: FOREIGN_BUSINESS_FAMILY.name,
  storeKey: FOREIGN_BUSINESS_FAMILY.storeKey,
};

export class Family extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      List: this.props.List,
      HasForeignFamily: this.props.HasForeignFamily,
      ...queue,
    });
  };

  updateHasForeignFamily = (values) => {
    this.update({
      HasForeignFamily: values,
      List: values.value === 'Yes' ? this.props.List : { items: [], branch: {} },
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  summary = (item, index) => {
    const obj = (item && item.Item) || {};
    const name = NameSummary(obj.Name);

    return Summary({
      type: i18n.t('foreign.business.family.collection.summary.item'),
      index,
      left: isPVQForm(this.props.formType) ? null : name,
      right: null,
      placeholder: i18n.t('foreign.business.family.collection.summary.unknown'),
    });
  };

  render() {
    const isPVQ = isPVQForm(this.props.formType);
    const { errors, peopleWhoKnowYou, List } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, foreignBusinessAddressNameFields);
    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    return isPVQ ? (
      <div
        className="section-content foreign-business-family"
        data-section={FOREIGN.key}
        data-subsection={FOREIGN_BUSINESS_FAMILY.key}
      >
        <h1 className="section-header">{i18n.t('foreign.pvq.subsection.business.family')}</h1>
        <Branch
          {...this.props.HasForeignFamily}
          name="has_foreign_family"
          attribute="HasForeignFamily"
          label={i18n.t('foreign.pvq.business.family.heading.title')}
          labelSize="h4"
          adjustFor="p"
          warning
          onUpdate={this.updateHasForeignFamily}
          required={this.props.required}
          onError={this.handleError}
          scrollIntoView={this.props.scrollIntoView}
        >
          <ul>
            <li>{i18n.t('foreign.pvq.business.family.heading.titleItem1')}</li>
            <li>{i18n.t('foreign.pvq.business.family.heading.titleItem2')}</li>
            <li>{i18n.t('foreign.pvq.business.family.heading.titleItem3')}</li>
            <li>{i18n.t('foreign.pvq.business.family.heading.titleItem4')}</li>
            <li>{i18n.t('foreign.pvq.business.family.heading.titleItem5')}</li>
          </ul>
        </Branch>
        {this.props.HasForeignFamily.value === 'Yes' && (
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            onUpdate={this.updateList}
            onError={this.handleError}
            errors={accordionErrors}
            summary={this.summary}
            description={i18n.t('foreign.pvq.business.family.collection.summary.title')}
            appendTitle={i18n.t('foreign.pvq.business.family.collection.appendTitle')}
            appendMessage={i18n.m('foreign.pvq.business.family.collection.appendMessage')}
            appendLabel={i18n.t('foreign.pvq.business.family.collection.append')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            additionalErrors={
              (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <FamilyItem
              name="Item"
              bind
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={this.props.formType}
            />
          </Accordion>
        )}
      </div>
    ) : (
      <div
        className="section-content foreign-business-family"
        data-section={FOREIGN.key}
        data-subsection={FOREIGN_BUSINESS_FAMILY.key}
      >
        <h1 className="section-header">{i18n.t('foreign.subsection.business.family')}</h1>
        <Branch
          name="has_foreign_family"
          attribute="HasForeignFamily"
          label={i18n.t('foreign.business.family.heading.title')}
          labelSize="h4"
          adjustFor="p"
          {...this.props.HasForeignFamily}
          warning
          onUpdate={this.updateHasForeignFamily}
          required={this.props.required}
          onError={this.handleError}
          scrollIntoView={this.props.scrollIntoView}
        >
          {i18n.t('foreign.business.family.para.branch')}
        </Branch>

        <Show when={this.props.HasForeignFamily.value === 'Yes'}>
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            onUpdate={this.updateList}
            onError={this.handleError}
            errors={accordionErrors}
            summary={this.summary}
            description={i18n.t('foreign.business.family.collection.summary.title')}
            appendTitle={i18n.t('foreign.business.family.collection.appendTitle')}
            appendMessage={i18n.m('foreign.business.family.collection.appendMessage')}
            appendLabel={i18n.t('foreign.business.family.collection.append')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            additionalErrors={
              (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <FamilyItem
              name="Item"
              bind
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Accordion>
        </Show>
      </div>
    );
  }
}

Family.defaultProps = {
  name: 'Family',
  HasForeignFamily: {},
  List: {},
  onUpdate: () => {},
  onError: (value, arr) => arr,
  section: 'foreign',
  subsection: 'business/family',
  dispatch: () => {},
  defaultState: true,
  scrollToBottom: '',
  errors: [],
};

export default connectSubsection(Family, sectionConfig);
